/**
 * RedeploymentReductionDetailV2.utils.js
 *
 * Pure helpers behind the Reduction Detail page — no React, no fetching —
 * so the CSV shaping can be read (and unit-tested) apart from the component
 * tree, same split as WorkforcePlanningV2.utils.
 */

/**
 * Flattens the ranked occupants into the Redeployment Analysis CSV rows —
 * one row per occupant, with status derived from their rank against the
 * cutoff. Callers pass occupants with nextRoles already resolved (mock rows
 * precompute them; the export handler fetches missing ones before building).
 */
export const buildReductionCsvRows = ({
  roleTitle,
  occupants,
  cutoff,
  availableEmployeeIds,
  shortlistReasons = new Map(),
}) =>
  occupants.map((occupant, index) => ({
    Role: roleTitle,
    Person: occupant.person?.name,
    "Employee ID": occupant.person?.employeeId ?? "—",
    "SMI %": occupant.matchPct,
    Status: index >= cutoff ? "At Risk" : "Retained",
    "Marked Available": availableEmployeeIds.has(occupant.person?.id) ? "Yes" : "No",
    "Reason for Shortlisting": shortlistReasons.get(occupant.person?.id) ?? "—",
    // m.sri is null (not 0) when that next role has no real score yet — same
    // "—" the on-screen table falls back to (see PersonNextRolesTable's SMI
    // Match column), so the export doesn't stringify null into "(null%)".
    "Top Suggested Next Role(s)":
      (occupant.person?.nextRoles ?? [])
        .slice(0, 3)
        .map((m) => `${m.role?.title} (${m.sri == null ? "—" : `${m.sri}%`})`)
        .join("; ") || "—",
  }));

/**
 * Ranks a role's occupants the way the Reduction Detail table (and its CSV
 * export) both order them: SMI descending only. Shortlisting does NOT
 * reorder anyone — a Retained employee shortlisted for redeployment keeps
 * their rank and Retained status (with their Reason for Shortlisting),
 * matching View Planning's saved-plan drill-down, which ranks the same way.
 * Shared with the Candidate Profile page, which re-derives the same ranking
 * to reproduce Reduction Detail's header when a profile is opened from it.
 */
export const orderReductionOccupants = (occupants) =>
  [...occupants].sort((a, b) => b.matchPct - a.matchPct);

/**
 * The ?target= query value as a number — NaN when the param is absent or
 * empty, so "no target set" stays distinct from an explicit 0 (Number(null)
 * would otherwise read as 0).
 */
export const parseReductionTarget = (value) => (value == null || value === "" ? NaN : Number(value));

/**
 * The at-risk cutoff: the explicit ?target= carried in the URL (from Role
 * View, a shared link, or the Candidate Profile replicating this header),
 * falling back to the role's current headcount when no target was set yet.
 * A target of 0 is a real target (everyone is at risk), not "unset".
 */
export const computeReductionCutoff = (role, target) =>
  role && Number.isFinite(target) && target >= 0 ? target : role?.currentPositionCount ?? 0;
